import * as React from "react"

import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { cn } from "@/lib/utils"

function SherpaDialogHeader({ className, ...props }: React.ComponentProps<typeof DialogHeader>) {
  return <DialogHeader className={cn("shrink-0 gap-4 text-left", className)} {...props} />
}

function SherpaDialogFooter({ className, ...props }: React.ComponentProps<typeof DialogFooter>) {
  return (
    <DialogFooter
      className={cn("mt-auto shrink-0 -mx-0 -mb-0 border-0 bg-transparent p-0 pt-8", className)}
      {...props}
    />
  )
}

function layoutDialogChildren(children: React.ReactNode): React.ReactNode[] {
  const result: React.ReactNode[] = []
  let bodyBuffer: React.ReactNode[] = []

  const flushBody = () => {
    if (bodyBuffer.length === 0) {
      return false
    }

    result.push(
      <div key={`sherpa-dialog-body-${result.length}`} className="min-h-0 flex-1 overflow-y-auto">
        {bodyBuffer}
      </div>
    )
    bodyBuffer = []
    return true
  }

  const pushSpacer = () => {
    result.push(<div key={`sherpa-dialog-spacer-${result.length}`} className="min-h-0 flex-1" aria-hidden />)
  }

  const visit = (node: React.ReactNode) => {
    React.Children.forEach(node, (child) => {
      if (!React.isValidElement(child)) {
        if (child != null && child !== false) {
          bodyBuffer.push(child)
        }
        return
      }

      if (child.type === React.Fragment) {
        visit((child.props as React.PropsWithChildren).children)
        return
      }

      if (child.type === SherpaDialogHeader) {
        flushBody()
        result.push(child)
        return
      }

      if (child.type === SherpaDialogFooter) {
        const hadBody = flushBody()
        if (!hadBody) {
          pushSpacer()
        }
        result.push(child)
        return
      }

      bodyBuffer.push(child)
    })
  }

  visit(children)
  flushBody()

  return result
}

function SherpaDialogContent({
  className,
  children,
  showCloseButton = true,
  ...props
}: React.ComponentProps<typeof DialogContent> & {
  showCloseButton?: boolean
}) {
  return (
    <DialogContent
      className={cn(
        "!flex h-[var(--sherpa-dialog-height)] max-h-[calc(100dvh-2rem)] w-full max-w-[calc(100%-2rem)] !flex-col !gap-0 overflow-hidden rounded-xl border-0 bg-white px-4 pb-6 pt-14 text-left text-[#2b2e34] shadow-none [--sherpa-dialog-height:min(545px,calc(100dvh-2rem))] sm:max-w-[695px] sm:px-8 sm:pb-8 sm:pt-10",
        "max-sm:inset-x-0 max-sm:top-auto max-sm:bottom-0 max-sm:left-0 max-sm:h-[min(545px,90dvh)] max-sm:max-h-[90dvh] max-sm:w-full max-sm:max-w-none max-sm:translate-x-0 max-sm:translate-y-0 max-sm:rounded-t-xl max-sm:rounded-b-none max-sm:[--sherpa-dialog-height:min(545px,90dvh)]",
        "max-sm:duration-300 max-sm:ease-out max-sm:data-closed:duration-200 max-sm:data-open:slide-in-from-bottom max-sm:data-closed:slide-out-to-bottom",
        "sm:data-open:zoom-in-95 sm:data-closed:zoom-out-95",
        className
      )}
      overlayClassName="bg-[rgba(43,46,52,0.4)] duration-300 data-closed:duration-200 supports-backdrop-filter:backdrop-blur-none"
      showCloseButton={false}
      {...props}
    >
      {showCloseButton ? (
        <DialogClose asChild>
          <button
            type="button"
            className="absolute top-4 right-4 inline-flex size-8 cursor-pointer items-center justify-center text-[#818796] transition-colors hover:text-[#2b2e34] sm:top-6 sm:right-6"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none" aria-hidden>
              <path d="M24 8L8 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M8 8L24 24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span className="sr-only">Close</span>
          </button>
        </DialogClose>
      ) : null}
      <div className="flex min-h-0 flex-1 flex-col">{layoutDialogChildren(children)}</div>
    </DialogContent>
  )
}

function SherpaDialogTitle({ className, ...props }: React.ComponentProps<typeof DialogTitle>) {
  return (
    <DialogTitle
      className={cn("max-w-[586px] text-[28px] leading-[32px] font-semibold text-[#2b2e34] sm:text-[48px] sm:leading-[52px]", className)}
      {...props}
    />
  )
}

function SherpaDialogDescription({ className, ...props }: React.ComponentProps<typeof DialogDescription>) {
  return (
    <DialogDescription
      className={cn("text-base leading-[22px] text-[#818796] sm:text-lg sm:leading-[26px]", className)}
      {...props}
    />
  )
}

export {
  Dialog as SherpaDialog,
  DialogClose as SherpaDialogClose,
  SherpaDialogContent,
  SherpaDialogDescription,
  SherpaDialogFooter,
  SherpaDialogHeader,
  SherpaDialogTitle,
  DialogTrigger as SherpaDialogTrigger,
}
